import React from "react";
import { Alert, AlertTitle, Box, Button, Stack, Typography } from "@mui/material";
import LoginIcon from "@mui/icons-material/Login";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { useNavigate } from "react-router-dom";
import {
  navigateToForgotUsername,
  navigateToLogin,
} from "./duplicateEmailNavigation";

/**
 * Shown above registration forms after /accounts-by-email runs on email blur.
 *
 * When hasPendingInvite is true, directs the user to their invite email instead
 * of suggesting sign-in (placeholder portal users often trigger false duplicates).
 */
const DuplicateEmailAlert = ({ accounts, email, hasPendingInvite = false }) => {
  const navigate = useNavigate();

  if (hasPendingInvite) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        <AlertTitle>Complete registration using your invite email</AlertTitle>
        <Typography variant="body2" sx={{ mb: 1 }}>
          A pending law firm invitation exists for{" "}
          <strong>{email || "this email address"}</strong>. Open the invite email
          from Probate Bond Pros and use the registration link there to finish
          setting up your account.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Do not use this public registration form — it cannot accept your invite.
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 1.5 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<MailOutlineIcon />}
            onClick={() => navigateToLogin(navigate, email)}
          >
            Return to sign in
          </Button>
        </Stack>
      </Alert>
    );
  }

  if (!accounts || accounts.length === 0) return null;

  return (
    <Alert severity="warning" sx={{ mb: 2 }}>
      <AlertTitle>An account already exists for this email</AlertTitle>
      <Typography variant="body2" sx={{ mb: 1 }}>
        We found {accounts.length} account{accounts.length === 1 ? "" : "s"} associated with{" "}
        <strong>{email}</strong>. To avoid creating a duplicate, please sign in or recover your
        username instead of registering again.
      </Typography>
      <Box sx={{ mb: 1 }}>
        {accounts.map((acc) => (
          <Typography key={acc.user_id} variant="caption" component="div" sx={{ color: "text.secondary" }}>
            • {acc.username}
            {acc.role ? ` — ${acc.role}` : ""}
            {acc.lawfirm_or_pg_label ? ` @ ${acc.lawfirm_or_pg_label}` : ""}
          </Typography>
        ))}
      </Box>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
        <Button
          size="small"
          variant="contained"
          startIcon={<LoginIcon />}
          onClick={() => navigateToLogin(navigate, email)}
        >
          Go to Sign In
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<HelpOutlineIcon />}
          onClick={() => navigateToForgotUsername(navigate, email)}
        >
          Forgot username?
        </Button>
      </Stack>
    </Alert>
  );
};

export default DuplicateEmailAlert;
